import { Modal } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BiCheck } from "react-icons/bi";
import { useSectionData, sectionText as text } from "./useSectionData";
import { pageBannerImage } from "../../../Components/layouts/usePageBanner";
import PageBannerLoading from "../../../Components/layouts/PageBannerLoading";
import { HOME_WOW_DURATION, homeWowDelay } from "../../../utils/homeWow";

type HomeSection = {
  video_url?: string | false;
  video_thumbnail_url?: string | false;
  small_title?: string | false;
  title?: string | false;
  description?: string | false;
  items?: unknown;
};

function playerUrl(raw: string): string {
  try {
    const url = new URL(raw);
    const host = url.hostname.toLowerCase();
    const youtube = host === "youtube.com" || host.endsWith(".youtube.com") || host === "youtu.be";
    if (youtube) {
      const id = host === "youtu.be"
        ? url.pathname.split("/")[1]
        : url.pathname.startsWith("/embed/") || url.pathname.startsWith("/shorts/") || url.pathname.startsWith("/live/")
          ? url.pathname.split("/")[2]
          : url.searchParams.get("v");
      if (id && /^[\w-]{11}$/.test(id)) {
        return `https://www.youtube.com/embed/${id}?autoplay=1`;
      }
    }
    if (host === "vimeo.com" || host === "www.vimeo.com" || host === "player.vimeo.com") {
      const id = url.pathname.split("/").filter(Boolean).pop();
      if (id && /^\d+$/.test(id)) return `https://player.vimeo.com/video/${id}?autoplay=1`;
    }
  } catch {
    // Relative media paths are resolved by pageBannerImage below.
  }
  return pageBannerImage(raw) ?? raw;
}

const Block1 = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisble] = useState(false);
  const section = useSectionData<HomeSection>("home-section");

  type BlockItemKey = `item${1 | 2 | 3 | 4 | 5 | 6}`;
  const staticKeys: readonly BlockItemKey[] = [
    "item1", "item2", "item3", "item4", "item5", "item6",
  ];

  const apiItems = (Array.isArray(section?.items) ? section!.items : [])
    .map(text)
    .filter((item): item is string => Boolean(item));
  const items = apiItems.length
    ? apiItems
    : staticKeys.map((key) => t(`home.block1.items.${key}`));
  const half = Math.ceil(items.length / 2);

  const eyebrow = text(section?.small_title) ?? t("home.block1.btn");
  const title = text(section?.title) ?? t("home.block1.title");
  const description = text(section?.description) ?? t("home.block1.description");
  const thumbnail =
    pageBannerImage(section?.video_thumbnail_url) ?? "assets/imgs/cta/cta-1/video.png";
  const videoUrl = text(section?.video_url);
  const embedUrl = videoUrl
    ? playerUrl(videoUrl)
    : "https://www.youtube.com/embed/ZHjhCMd7PPs?autoplay=1";
  // ponytail: hosted players go in an iframe, anything else is a plain file.
  const isEmbed = !videoUrl || /youtube|youtu\.be|vimeo/i.test(videoUrl);

  const renderList = (list: string[]) => (
    <ul className="ac-block-cta__list">
      {list.map((label, i) => (
        <li key={`${label}-${i}`}>
          <span className="ac-block-cta__check" aria-hidden>
            <BiCheck size={14} strokeWidth={2.5} />
          </span>
          {label}
        </li>
      ))}
    </ul>
  );

  if (section === undefined) return <PageBannerLoading />;

  return (
    <section className="box-cta-1 ac-block-cta py-96">
      <div className="container ac-block-cta__container">
        <div className="row align-items-center ac-block-cta__row">
          <div
            className="col-lg-6 ac-block-cta__col ac-block-cta__col--media wow fadeInUp"
            data-wow-duration={HOME_WOW_DURATION}
            data-wow-delay={homeWowDelay(0)}
          >
            <div className="card-video ac-block-cta__video">
              <div className="card-image">
                <button
                  type="button"
                  className="btn ac--btn-play border-0 bg-transparent p-0"
                  onClick={() => setIsVisble(true)}
                  aria-label={eyebrow}
                />
                <img src={thumbnail} alt="" loading="lazy" />
              </div>
            </div>
          </div>
          <div
            className="col-lg-6 ac-block-cta__col ac-block-cta__col--copy mt-lg-0 mt-4 wow fadeInUp"
            data-wow-duration={HOME_WOW_DURATION}
            data-wow-delay={homeWowDelay(1)}
          >
            <div className="ac-block-cta__body">
              <span className="ac-block-cta__eyebrow">{eyebrow}</span>
              <h5 className="ac-block-cta__title neutral-1000 ac--font-expo ac--color-light-dark">
                {title}
              </h5>
              <h6 className="ac-block-cta__lede neutral-1000 ac--font-expo mb-0">
                <span className="ac--color-light-gray">
                  {t("home.block1.subtitle1")}
                </span>{" "}
                <span className="ac--color-primary">
                  {t("home.block1.subtitle2")}
                </span>
              </h6>
              <p className="ac-block-cta__copy neutral-500 ac--font-expo">
                {description}
              </p>
              <div className="row ac-block-cta__features">
                <div className="col-md-6 col-sm-12">
                  {renderList(items.slice(0, half))}
                </div>
                <div className="col-md-6 col-sm-12">
                  {renderList(items.slice(half))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal
        title={null}
        open={isVisible}
        onCancel={() => setIsVisble(false)}
        centered
        width="min(92vw, 56rem)"
        footer={null}
        className="ac--modal-video"
        classNames={{ mask: "ac-modal-video__mask" }}
        styles={{ body: { padding: 0 } }}
        destroyOnClose
        closable={{ "aria-label": t("services.close") }}
      >
        <div className="ac-modal-video__shell">
          <div className="ac-modal-video__aspect">
            {isEmbed ? (
              <iframe
                className="ac-modal-video__iframe"
                src={embedUrl ?? undefined}
                title={t("home.block1.videoTitle")}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            ) : (
              <video
                className="ac-modal-video__iframe"
                src={embedUrl ?? undefined}
                title={t("home.block1.videoTitle")}
                controls
                autoPlay
              />
            )}
          </div>
        </div>
      </Modal>
    </section>
  );
};

export default Block1;
